import {areSetsEqual} from '@benchling/js-utils';

import {pluralize} from 'BenchlingUtil';
import notifications from 'notifications';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {CreateStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {checkClipboardReadAccess} from 'util/clipboard/ClipboardUtil';

import {COPY_STEPS_CLIPBOARD_CONTENT_TYPE, CopyStepsClipboardContent} from './copyStepsToClipboard';

const COULD_NOT_READ_CLIPBOARD_MSG = 'Unable to read from clipboard.';
const INVALID_CLIPBOARD_CONTENT_MSG = 'Could not paste steps, because the clipboard does not contain steps.';

function isCopyStepsClipboardContent(value: unknown): value is CopyStepsClipboardContent {
  if (
    value == null ||
    typeof value !== 'object' ||
    !areSetsEqual(new Set(Object.keys(value)), new Set(['type', 'steps']))
  ) {
    return false;
  }
  const {type, steps} = value as CopyStepsClipboardContent;
  return (
    type === COPY_STEPS_CLIPBOARD_CONTENT_TYPE &&
    steps.every(
      (step: unknown) => typeof step === 'object' && step != null && step.hasOwnProperty('stepType')
    )
  );
}

/**
 * Read steps from clipboard, and notify the user if the clipboard content cannot be read or is invalid.
 *
 * @returns Steps, or null if the clipboard content could not be read or is invalid.
 */
async function readStepsFromClipboard(): Promise<Array<CreateStepParams> | null> {
  try {
    // `checkClipboardReadAccess` checks clipboard access, and shows a notification if access is denied.
    const hasClipboardReadAccess = await checkClipboardReadAccess();
    if (!hasClipboardReadAccess) {
      return null;
    }

    const text = await navigator.clipboard.readText();
    const json = JSON.parse(text);
    if (isCopyStepsClipboardContent(json) && json.steps.length > 0) {
      return json.steps;
    }
  } catch (e) {
    // `DOMException` is raised if the clipboard content could not be read; reading clipboard content may
    // fail even if `checkClipboardReadAccess` returns `true`.
    if (e instanceof DOMException) {
      notifications.error(COULD_NOT_READ_CLIPBOARD_MSG);
      return null;
    }
    // `SyntaxError` is raised if the clipboad content is not JSON; all other exceptions are unexpected.
    if (!(e instanceof SyntaxError)) {
      throw e;
    }
  }
  // The content is invalid: not JSON, not `CopyStepsClipboardContent`, or empty.
  notifications.error(INVALID_CLIPBOARD_CONTENT_MSG);
  return null;
}

/**
 * Paste steps from the user's clipboard into the pipeline.
 *
 * @param pipeline - The pipeline to paste steps into.
 */
export async function pasteStepsFromClipboard(
  pipeline: Pick<PipelineGraphQL, 'steps' | 'id'>
): Promise<void> {
  const steps = await readStepsFromClipboard();
  if (steps == null || steps.length === 0) {
    return;
  }

  const {newStepIds} = await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: steps,
    stepsToUpdate: [],
  });
  notifications.success(`Pasted ${newStepIds.length} ${pluralize(newStepIds.length, 'step')} from clipboard`);
}
